// Reviews — the individual ratings behind the averages.
//
// Its own section rather than an expansion of Ratings, and on both tabs: the aggregate
// answers "is this driver good", these answer "what happened", and only the second one
// ever gets read aloud to someone on a call.
//
// One component for both directions. `GET /users/:id/reviews` returns `{ asDriver,
// asRider }` from one id — `rated_user` keys both halves — so the Driver tab and the
// Rider tab read the same response through a `direction` prop rather than through two
// near-identical cards.
//
// Not in redux. This is one read with no writes against it and no other consumer, which
// is the same reason FleetOperatorCard next to it calls its API directly: a slice, three
// action types and a reducer case would all exist to hold a value nothing else looks at.
import { useCallback, useEffect, useRef, useState } from 'react';
import { StarIcon } from '../../../ui/icons';
import { scrollY } from '../../../ui/styles';
import { Box } from '../../../ui/core';
import { useFormatters } from '../../../hooks';
import api from '../../../lib/api';
import { SectionCard, EmptyRows } from '../../../pages/DriverProfile/components';
import { ReviewRow, TrailingMeta, Meta, SectionSkeleton, SectionError } from '../../../pages/UserProfile/parts';

const UserReviewsCard = ({ user, direction = 'driver' }) => {
  const f = useFormatters();
  const [section, setSection] = useState(null);
  const [status, setStatus] = useState('loading');
  // Same counter trick as FleetOperatorCard: bumped per request and by the effect's
  // cleanup, so a response cannot land after unmount or after `user.id` moved on.
  const request = useRef(0);

  const load = useCallback(async () => {
    const seq = (request.current += 1);
    setStatus('loading');
    try {
      const data = await api.users.getUserReviews({ id: user.id });
      if (seq !== request.current) return;
      setSection(direction === 'driver' ? data?.asDriver : data?.asRider);
      setStatus('done');
    } catch {
      if (seq === request.current) setStatus('failed');
    }
  }, [user.id, direction]);

  useEffect(() => {
    load();
    return () => {
      request.current += 1;
    };
  }, [load]);

  const rows = section?.rows ?? [];

  return (
    <SectionCard
      title="Reviews"
      icon={<StarIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
      meta={
        // Both numbers, because they answer different questions: `returned` is how much
        // of the history you are looking at, `written` is how much of it says anything.
        status === 'done' && section ? (
          <TrailingMeta>
            <Meta>
              {section.returned} recent · {section.written} written
            </Meta>
          </TrailingMeta>
        ) : null
      }
    >
      {status === 'loading' && <SectionSkeleton rows={3} />}
      {status === 'failed' && <SectionError onRetry={load}>Couldn&rsquo;t load reviews.</SectionError>}
      {status === 'done' && rows.length === 0 && (
        <EmptyRows>{direction === 'driver' ? 'No rider has rated them yet.' : 'Nobody has rated them yet.'}</EmptyRows>
      )}
      {status === 'done' && rows.length > 0 && (
        // Capped rather than paged: the endpoint returns the 25 newest per direction and
        // there is no cursor behind it, so a pager would be a control over one page.
        <Box sx={{ maxHeight: 420, ...scrollY }}>
          {rows.map((review, i) => (
            <ReviewRow
              key={review.id}
              first={i === 0}
              kind={direction}
              review={review}
              at={f.date.toLocaleString(review.createdAt, f.date.DateFormats.DATE_SHORT)}
            />
          ))}
        </Box>
      )}
    </SectionCard>
  );
};

export default UserReviewsCard;
